import { Text, Link, TextButton, Flex, Icon } from "@amzn/storm-ui";
import { sparkle } from "@amzn/storm-ui-icons";
import pluralize from "pluralize";
import React from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { navigationStore } from "../../store/navigation";
import { TopNavBarTabs } from "../../types/navigation";
import { AiLoadingIcon } from "../discovery/commonStyles";

export const DownloadReadyNotification: React.FunctionComponent<{
    name: string;
    downloadUrl: string;
}> = ({ name, downloadUrl }) => {
    return (
        <Text>
            <strong>{name}</strong> insight created |{" "}
            <Link href={downloadUrl} target="_blank">
                Download
            </Link>
            .
        </Text>
    );
};

export const DownloadFailedNotification: React.FunctionComponent<{
    name: string;
}> = ({ name }) => {
    return (
        <Text>
            <strong>{name}</strong> insight download failed.
        </Text>
    );
};

export const DownloadLoadingNotification: React.FunctionComponent<{
    name: string;
}> = ({ name }) => {
    return (
        <Text>
            Generating 1 new <strong>{name}</strong> insight.
        </Text>
    );
};

export const GeneratingMultipleInsightDecksNotification: React.FunctionComponent<{
    count: number;
}> = ({ count }) => {
    return (
        <Text>
            Generating <strong>{count}</strong> packaged insights.
        </Text>
    );
};

export const GeneratingSingleInsightDeckNotification: React.FunctionComponent<{
    name: string;
}> = ({ name }) => {
    return (
        <Text>
            Creating 1 new <strong>{name}</strong> insight.
        </Text>
    );
};

export const AddedToWorkbenchNotification: React.FunctionComponent<{
    slidesAdded: number;
    totalSlidesInWorkbench: number;
}> = ({ slidesAdded, totalSlidesInWorkbench }) => {
    return (
        <Text data-testid="add-to-workbench-notification">
            {pluralize("slide", slidesAdded, true)} was successfully added to
            workbench. Your workbench has {totalSlidesInWorkbench} total{" "}
            {pluralize("slide", totalSlidesInWorkbench)}.
        </Text>
    );
};

export const UpdateToWorkbenchNotification: React.FunctionComponent<{
    slideCountChange: number;
    totalSlidesInWorkbench: number;
}> = ({ slideCountChange, totalSlidesInWorkbench }) => {
    const changeText = ` with ${pluralize("slide", Math.abs(slideCountChange), true)} ${slideCountChange > 0 ? "added" : "removed"}`;

    return (
        <Text data-testid="update-to-workbench-notification">
            Update Successful{slideCountChange !== 0 && changeText}. Your
            workbench has {totalSlidesInWorkbench} total{" "}
            {pluralize("slide", totalSlidesInWorkbench)}.
        </Text>
    );
};

export const CreateFeedbackNotificationMessage: React.FunctionComponent<{
    ticketId: string;
}> = ({ ticketId }) => {
    return (
        <Text inline>
            <Text inline>Issue has been created. Please use this</Text>{" "}
            <Link
                target="_blank"
                href={`https://t.corp.amazon.com/${ticketId}`}
            >
                Ticket
            </Link>{" "}
            <Text inline>to track</Text>
        </Text>
    );
};

export const ContentSearchErrorNotificationMessage: React.FunctionComponent<{
    errors: string[];
}> = ({ errors }) => {
    const hasCmsUserError = errors.some((error) =>
        error.includes("The requesting user is not a valid CMS user"),
    );

    return (
        <>
            <Text style={{ marginBottom: 15 }}>
                <Text inline>
                    An error has occured when retrieving contents. If this issue
                    persists, please open a ticket using the "Report an Issue"
                    button at the top of the page. Please include the following
                    lines to help our engineering team debug the issue:
                </Text>
            </Text>
            {errors.map((e) => {
                return <Text>{e}</Text>;
            })}
            {hasCmsUserError && (
                <Text style={{ marginTop: 15 }}>
                    <Text inline>Please ask your manager to add you to: </Text>
                    <Link
                        target="_blank"
                        href="https://permissions.amazon.com/a/team/ihub-acc-content-viewers"
                    >
                        https://permissions.amazon.com/a/team/ihub-acc-content-viewers
                    </Link>
                </Text>
            )}
        </>
    );
};

export const GeneratingExploreWithAINotification: React.FC = () => {
    return (
        <Flex columnGap="xxsmall" alignItems="center">
            <AiLoadingIcon />
            <Text>Generating Explore With AI insights...</Text>
        </Flex>
    );
};

const SuccessExploreWithAIIcon = styled(Icon)`
    color: ${({ theme }) => theme.color.purple[800]};
`;

export const SuccessExploreWithAINotification: React.FC = () => {
    const [setActiveTab] = useStore(navigationStore, (state) => [
        state.setActiveTab,
    ]);

    return (
        <Flex columnGap="xsmall" alignItems="center">
            <SuccessExploreWithAIIcon
                data-testid="success-explore-with-ai-icon"
                type={sparkle}
            />
            <Text data-testid="success-explore-with-ai-text">
                Explore With AI Insights generated successfully!
                <br />
                Click{" "}
                <TextButton
                    data-testid="success-explore-with-ai-navigate-button"
                    onClick={() => {
                        setActiveTab(TopNavBarTabs.EXPLORE_WITH_AI);
                    }}
                >
                    here
                </TextButton>{" "}
                to view
            </Text>
        </Flex>
    );
};

export const AddedToGlobalWorkbenchNotification: React.FunctionComponent<{
    slidesAdded: number;
}> = ({ slidesAdded }) => {
    return (
        <Text data-testid="add-to-workbench-notification">
            {pluralize("slide", slidesAdded, true)} added to your workbench
            cart.
        </Text>
    );
};

export const UpdateGlobalWorkbenchNotification: React.FunctionComponent =
    () => {
        return (
            <Text data-testid="update-workbench-notification">
                Your workbench cart has been updated.
            </Text>
        );
    };

export const GlobalWorkbenchCartActionErrorNotification: React.FunctionComponent =
    () => {
        return (
            <Text data-testid="workbench-action-error-notification">
                There is an issue updating your workbench cart. Please try again
                later or use "Report an Issue" at the top of the page if this
                problem persists.
            </Text>
        );
    };

export const DownloadUrlFailedNotification: React.FunctionComponent = () => {
    return (
        <Text data-testid="download-url-failed-notification">
            Failed to download the content. Please contact us using "Report an
            issue" feature at the top to help us investigate.
        </Text>
    );
};
